import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Database, ShieldCheck, Plus, Building2, PauseCircle, PlayCircle } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA' };

export default function TenantsPage() {
  const api = useApi();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: '', admin_name: '', admin_email: '', admin_password: '' });
  const [saving, setSaving] = useState(false);
  const [isolation, setIsolation] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/tenants').then(r => setData(r.data))
      .catch(e => setData({ error: e?.response?.status === 403 ? 'forbidden' : 'error' }))
      .finally(() => setLoading(false));
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const { data: t } = await api.post('/admin/tenants', form);
      toast.success(t.message || 'Tenant provisioned');
      setShowForm(false);
      setForm({ name: '', admin_name: '', admin_email: '', admin_password: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Provisioning failed'); }
    setSaving(false);
  };

  const setStatus = async (id, status) => {
    try {
      await api.post(`/admin/tenants/${id}/status`, { status });
      toast.success(status === 'suspended' ? 'Workspace suspended — logins blocked' : 'Workspace reactivated');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const runIsolationCheck = async () => {
    try {
      const { data: r } = await api.get('/admin/tenants/isolation-check');
      setIsolation(r);
      toast.success(r.isolated ? 'Isolation verified — every tenant on a separate database' : 'ISOLATION ISSUE DETECTED');
    } catch (e) { toast.error('Check failed'); }
  };

  if (loading) return <Skeleton className="h-64 rounded-sm" />;
  if (user?.role !== 'platform_owner' || data?.error === 'forbidden') {
    return <p className="text-sm p-6" style={{ color: C.slate }} data-testid="tenants-forbidden">Tenant management is restricted to the platform owner.</p>;
  }
  if (!data?.tenants) return <p className="text-sm p-6" style={{ color: C.slate }}>Unavailable.</p>;

  return (
    <div className="max-w-4xl space-y-4" data-testid="tenants-page">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: 'rgba(0,180,216,0.12)' }}>
          <Database className="w-5 h-5" style={{ color: C.teal }} />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold" style={{ color: C.navy }}>Tenant Workspaces</h2>
          <p className="text-xs mt-0.5" style={{ color: C.slate }}>
            Database-per-tenant isolation — each MGA client gets a physically separate database. Their users sign in
            at the same login page and are routed to their own workspace automatically. ARIA automation runs per tenant.
          </p>
        </div>
        <div className="flex gap-2 shrink-0">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={runIsolationCheck} data-testid="isolation-check-btn">
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Verify Isolation
          </Button>
          <Button size="sm" className="rounded-sm text-white" style={{ background: C.blue }} onClick={() => setShowForm(s => !s)} data-testid="new-tenant-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Tenant
          </Button>
        </div>
      </div>

      {isolation && (
        <Card className="p-3.5 rounded-sm flex items-center gap-2.5" data-testid="isolation-result"
          style={{ background: isolation.isolated ? 'rgba(29,158,117,0.06)' : 'rgba(226,75,74,0.06)', border: `1px solid ${isolation.isolated ? 'rgba(29,158,117,0.35)' : 'rgba(226,75,74,0.4)'}` }}>
          <ShieldCheck className="w-4 h-4" style={{ color: isolation.isolated ? C.green : C.red }} />
          <p className="text-xs" style={{ color: isolation.isolated ? C.green : C.red }}>
            <b>{isolation.isolated ? 'ISOLATED' : 'ISOLATION FAILURE'}</b> — {isolation.tenants.length} tenant(s), each on a distinct database ({isolation.tenants.map(t => t.db_name).join(', ')})
          </p>
        </Card>
      )}

      {showForm && (
        <Card className="p-5 rounded-sm bg-white space-y-3" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid="tenant-form">
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Provision a new MGA workspace</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="MGA name (e.g. Wattle Demo Underwriting)" className="rounded-sm" data-testid="tenant-name-input" />
            <Input value={form.admin_name} onChange={e => setForm(f => ({ ...f, admin_name: e.target.value }))} placeholder="Admin full name" className="rounded-sm" data-testid="tenant-admin-name-input" />
            <Input value={form.admin_email} onChange={e => setForm(f => ({ ...f, admin_email: e.target.value }))} placeholder="Admin email (their login)" className="rounded-sm" data-testid="tenant-admin-email-input" />
            <Input type="password" value={form.admin_password} onChange={e => setForm(f => ({ ...f, admin_password: e.target.value }))} placeholder="Admin password (min 8 chars)" className="rounded-sm" data-testid="tenant-admin-password-input" />
          </div>
          <Button onClick={create} disabled={saving || !form.name || !form.admin_name || !form.admin_email || form.admin_password.length < 8}
            className="rounded-sm text-white" style={{ background: C.green }} data-testid="tenant-create-btn">
            {saving ? 'Provisioning…' : 'Provision Workspace'}
          </Button>
        </Card>
      )}

      <div className="space-y-3">
        {data.tenants.map(t => (
          <Card key={t.tenant_id} className="p-4 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.15)' }} data-testid={`tenant-card-${t.tenant_id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <Building2 className="w-4 h-4 shrink-0" style={{ color: t.is_primary ? C.blue : C.teal }} />
              <div className="flex-1 min-w-[180px]">
                <p className="text-sm font-semibold" style={{ color: C.navy }}>{t.name}</p>
                <p className="text-[11px] font-mono" style={{ color: C.slate }}>db: {t.db_name}{t.admin_email ? ` · admin: ${t.admin_email}` : ''}</p>
              </div>
              <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase"
                style={{ background: (t.status === 'active' ? C.green : C.red) + '15', color: t.status === 'active' ? C.green : C.red }}
                data-testid={`tenant-status-${t.tenant_id}`}>{t.status}</span>
              {!t.is_primary && (t.status === 'active' ? (
                <button onClick={() => setStatus(t.tenant_id, 'suspended')} data-testid={`tenant-suspend-${t.tenant_id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-semibold border" style={{ color: C.red, borderColor: 'rgba(226,75,74,0.35)' }}>
                  <PauseCircle className="w-3.5 h-3.5" /> Suspend
                </button>
              ) : (
                <button onClick={() => setStatus(t.tenant_id, 'active')} data-testid={`tenant-activate-${t.tenant_id}`}
                  className="flex items-center gap-1 px-2 py-1 rounded-sm text-[11px] font-semibold border" style={{ color: C.green, borderColor: 'rgba(29,158,117,0.35)' }}>
                  <PlayCircle className="w-3.5 h-3.5" /> Activate
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 mt-3">
              {t.stats_unavailable ? (
                <p className="col-span-full text-[11px] py-1.5 px-2 rounded-sm" style={{ background: 'rgba(232,160,32,0.08)', color: '#B27516' }} data-testid={`tenant-stats-unavailable-${t.tenant_id}`}>
                  Stats unavailable — tenant database not reachable from this deployment
                </p>
              ) : Object.entries(t.stats || {}).map(([k, v]) => (
                <div key={k} className="text-center py-1.5 rounded-sm" style={{ background: C.bg }}>
                  <p className="text-[9px] uppercase tracking-wider" style={{ color: C.slate }}>{k}</p>
                  <p className="text-sm font-semibold font-mono" style={{ color: C.navy }}>{Number(v).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
